/**
 * Wishlist Drawer Component
 * Right-side slide-in drawer for wishlist management
 * NO REDIRECTS - Instant move-to-cart conversion
 * NO COLOR LOGIC - Single color product system
 * Mobile-first design
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useWishlist } from '../../../context/WishlistContext';
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import { formatCurrency } from '../../../utils/currency';
import { moveToCart, analyzeProductForMoveToCart, getInStockSizes, shouldShowSizePicker } from '../../../services/wishlistToCartService';
import {
  IconClose,
  IconHeart,
  IconCheck,
  IconLock,
} from '../../common/Icons/Icons';
import SizeSelectBottomSheet from './SizeSelectBottomSheet';
import ApiImage from '../../common/ApiImage';
import './WishlistDrawer.css';

function WishlistDrawer({ isOpen, onClose }) {
  const { wishlist, removeFromWishlist, loading: wishlistLoading, refreshWishlist } = useWishlist();
  const { refreshCart, openCart, updateCartFromResponse } = useCart();
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const drawerRef = useRef(null);

  // Close drawer when navigating to login so login page is not behind the drawer (e.g. on phone)
  useEffect(() => {
    if (pathname === '/login' && isOpen) onClose();
  }, [pathname, isOpen, onClose]);
  
  const [itemErrors, setItemErrors] = useState({});
  const [itemLoading, setItemLoading] = useState({});
  const [toastMessage, setToastMessage] = useState(null);
  /** Product item for which the size-picker bottom sheet is open (null = closed) */
  const [sizePickerItem, setSizePickerItem] = useState(null);

  // Lock body scroll when drawer is open (align with CartDrawer / .no-scroll pattern)
  useEffect(() => {
    document.body.classList.toggle('no-scroll', isOpen);
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isOpen]);

  // Focus trap within drawer when open (keyboard users stay inside)
  useEffect(() => {
    if (!isOpen || !drawerRef.current) return;
    const el = drawerRef.current;
    const selector = 'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const getNodes = () => Array.from(el.querySelectorAll(selector));

    const focusTimer = setTimeout(() => {
      const nodes = getNodes();
      if (nodes.length > 0) {
        try {
          nodes[0].focus({ preventScroll: true });
        } catch {
          nodes[0].focus();
        }
      }
    }, 260);

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const nodes = getNodes();
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    el.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      el.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  /**
   * Move product from wishlist to cart
   * If product has multiple in-stock sizes, opens size picker first; otherwise moves with stored/default size.
   */
  const handleMoveToCart = async (e, item, selectedSizeFromPicker = null) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      onClose(); // Close drawer first so login page is visible (especially on mobile)
      navigate('/login?redirect=cart');
      return;
    }

    const product = item;
    let productId = product.id || product._id;
    if (productId && typeof productId !== 'string') {
      productId = String(productId);
    }

    if (!productId) {
      setItemErrors(prev => ({ ...prev, [productId || 'unknown']: 'Invalid product' }));
      return;
    }

    const analysis = analyzeProductForMoveToCart(product);
    if (!analysis.canMove) {
      setItemErrors(prev => ({ ...prev, [productId]: analysis.reason }));
      setTimeout(() => {
        setItemErrors(prev => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });
      }, 3000);
      return;
    }

    // Multiple in-stock sizes and no size chosen yet → open size picker (don't call API yet)
    if (!selectedSizeFromPicker && shouldShowSizePicker(product)) {
      setSizePickerItem(item);
      return;
    }

    setItemLoading(prev => ({ ...prev, [productId]: true }));
    setItemErrors(prev => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
    if (sizePickerItem && (sizePickerItem.id || sizePickerItem._id) === productId) {
      setSizePickerItem(null);
    }

    try {
      const sizeToUse = selectedSizeFromPicker != null ? selectedSizeFromPicker : (item.size || 'S');
      const result = await moveToCart(productId, token, sizeToUse);

      if (result.success) {
        if (result.cart) {
          updateCartFromResponse(result.cart);
        } else {
          await refreshCart();
        }
        await refreshWishlist();
        setToastMessage('Moved to Cart');
        onClose();
        setTimeout(() => openCart(), 150);
      }
    } catch (error) {
      setItemErrors(prev => ({ ...prev, [productId]: error.message || 'Failed to move to cart' }));
      setTimeout(() => {
        setItemErrors(prev => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });
      }, 3000);
    } finally {
      setItemLoading(prev => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  /**
   * Called when user confirms size in the size-picker sheet: add to cart with chosen size.
   */
  const handleAddToCartWithSize = async (selectedSize) => {
    if (!sizePickerItem) return;
    const productId = sizePickerItem.id || sizePickerItem._id;
    setItemLoading(prev => ({ ...prev, [productId]: true }));
    setItemErrors(prev => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });

    try {
      const result = await moveToCart(String(productId), token, selectedSize);
      if (result.success) {
        if (result.cart) updateCartFromResponse(result.cart);
        else await refreshCart();
        await refreshWishlist();
        setSizePickerItem(null);
        setToastMessage('Moved to Cart');
        onClose();
        setTimeout(() => openCart(), 150);
      }
    } catch (error) {
      setItemErrors(prev => ({ ...prev, [productId]: error.message || 'Failed to move to cart' }));
    } finally {
      setItemLoading(prev => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  /**
   * Remove product from wishlist
   */
  const handleRemove = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Backend returns product data directly
    const product = item;
    const productId = product.id || product._id;
    
    await removeFromWishlist(productId);
  };

  // Memoize stock calculation for performance
  const wishlistWithStock = useMemo(() => {
    return wishlist.map(item => {
      // Backend returns product data directly
      const product = item;
      const isOutOfStock = product.variants && product.variants.length > 0
        ? product.variants.every(v => (v.stock || 0) === 0)
        : product.availability === 'out_of_stock';
      
      return {
        ...item,
        isOutOfStock,
      };
    });
  }, [wishlist]);

  return (
    <>
      {/* Toast Notification */}
      {toastMessage && (
        <div className="wishlist-drawer__toast" role="status" aria-live="polite">
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Backdrop */}
      <div
        className={`wishlist-drawer__overlay${isOpen ? ' wishlist-drawer__overlay--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`wishlist-drawer ${isOpen ? 'wishlist-drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Wishlist, ${wishlist.length} ${wishlist.length === 1 ? 'item' : 'items'}`}
      >
        {/* Header */}
        <div className="wishlist-drawer__header">
          <h2 className="wishlist-drawer__title">
            My Wishlist
            <span className="wishlist-drawer__count">
              ({wishlist.length} {wishlist.length === 1 ? 'item' : 'items'})
            </span>
          </h2>
          <button
            className="wishlist-drawer__close"
            onClick={onClose}
            aria-label="Close wishlist"
          >
            <IconClose size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="wishlist-drawer__content">
          {wishlistLoading ? (
            <div className="wishlist-drawer__loading">
              <div className="wishlist-drawer__skeleton">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="wishlist-drawer__skeleton-item" />
                ))}
              </div>
            </div>
          ) : wishlist.length === 0 ? (
            <div className="wishlist-drawer__empty">
              <IconHeart size={48} className="wishlist-drawer__empty-icon" />
              <p className="wishlist-drawer__empty-text">Your wishlist is empty</p>
              <p className="wishlist-drawer__empty-subtext">Start saving pieces you love</p>
              <div className="wishlist-drawer__empty-cta-group">
                <Link to="/shop/women" className="wishlist-drawer__empty-cta" onClick={onClose}>
                  Shop Women
                </Link>
                <Link to="/shop/men" className="wishlist-drawer__empty-cta" onClick={onClose}>
                  Shop Men
                </Link>
              </div>
            </div>
          ) : (
            <div className="wishlist-drawer__items">
              {wishlistWithStock.map((item) => {
                // Backend returns unified snapshot: name, price, image, comparePrice, size, etc.
                const product = item;
                const productId = product.id || product.productId || product._id;
                const productName = product.name || 'Product';
                const productImage = product.image ||
                                   product.images?.[0]?.secure_url ||
                                   product.images?.[0]?.url ||
                                   (typeof product.images?.[0] === 'string' ? product.images[0] : null) ||
                                   '/hero.png';
                const productPrice = Number(product.price) || 0;
                const compareAtPrice = product.comparePrice != null ? Number(product.comparePrice) : (product.compareAtPrice != null ? Number(product.compareAtPrice) : null);
                const isOnSale = compareAtPrice && compareAtPrice > productPrice;
                const discountPercent = isOnSale
                  ? Math.round(((compareAtPrice - productPrice) / compareAtPrice) * 100)
                  : 0;
                const isOutOfStock = product.isOutOfStock;
                const storedSize = item.size || 'S'; // Backend returns size at top level
                const itemError = itemErrors[productId];
                const isLoading = itemLoading[productId];

                const productHref = `/product/${product.slug || productId}`;

                // Analyze product for move to cart
                const analysis = analyzeProductForMoveToCart(product);
                const canMove = analysis.canMove && !isOutOfStock;

                return (
                  <div key={productId} className="wishlist-drawer__item">
                    {/* Product Image */}
                    <div className="wishlist-drawer__item-image-wrapper">
                      <Link to={productHref} className="wishlist-drawer__item-image-link">
                        <ApiImage
                          src={productImage}
                          fallback="/hero.png"
                          alt={productName}
                          className="wishlist-drawer__item-image"
                          loading="lazy"
                        />
                        {isOutOfStock && (
                          <span className="wishlist-drawer__oos-badge">Out of Stock</span>
                        )}
                      </Link>
                    </div>

                    {/* Product Details */}
                    <div className="wishlist-drawer__item-details">
                      <h3 className="wishlist-drawer__item-name">
                        <Link to={productHref} className="wishlist-drawer__item-name-link">
                          {productName}
                        </Link>
                      </h3>
                      
                      {/* Size Display */}
                      <div className="wishlist-drawer__item-size">
                        Size: <strong>{storedSize}</strong>
                      </div>

                      {/* Price */}
                      <div className="wishlist-drawer__item-price-row">
                        {productPrice > 0 ? (
                          <>
                            <span className="wishlist-drawer__item-price price-default">
                              {formatCurrency(productPrice)}
                            </span>
                            {isOnSale && (
                              <>
                                <span className="wishlist-drawer__item-compare price-struck">
                                  {formatCurrency(compareAtPrice)}
                                </span>
                                <span className="wishlist-drawer__item-sale-badge">
                                  {discountPercent}% OFF
                                </span>
                              </>
                            )}
                          </>
                        ) : (
                          <span className="wishlist-drawer__item-price price-default">
                            Price not available
                          </span>
                        )}
                      </div>

                      {/* Error Message */}
                      {itemError && (
                        <p className="wishlist-drawer__item-error" role="alert">
                          {itemError}
                        </p>
                      )}

                      {/* Action Buttons */}
                      <div className="wishlist-drawer__item-actions">
                        <button
                          type="button"
                          className="wishlist-drawer__move-cart btn-primary"
                          onClick={(e) => handleMoveToCart(e, item)}
                          disabled={isOutOfStock || isLoading || !canMove}
                          aria-busy={isLoading}
                          title={!canMove && !isOutOfStock ? `Cannot move: ${analysis.reason || 'Unknown reason'}` : ''}
                        >
                          {isLoading ? (
                            <span className="wishlist-drawer__loading-spinner">Moving...</span>
                          ) : isOutOfStock ? (
                            'Out of Stock'
                          ) : !canMove ? (
                            analysis.reason || 'Cannot Move'
                          ) : (
                            'Move to Cart'
                          )}
                        </button>
                        <button
                          type="button"
                          className="wishlist-drawer__remove"
                          onClick={(e) => handleRemove(e, item)}
                          aria-label={`Remove ${productName} from wishlist`}
                        >
                          <IconClose size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {wishlist.length > 0 && (
          <div className="wishlist-drawer__footer">
            <div className="wishlist-drawer__trust">
              <span>
                <IconCheck size={12} />
                Quick checkout
              </span>
              <span>
                <IconLock size={12} />
                Secure payment
              </span>
            </div>
            <Link to="/wishlist" className="wishlist-drawer__view-all" onClick={onClose}>
              View Full Wishlist →
            </Link>
            {!isAuthenticated && (
              <p className="wishlist-drawer__guest-note">
                <Link to="/login" onClick={onClose}>Sign in</Link> to sync across devices
              </p>
            )}
          </div>
        )}
      </div>

      {/* Size selection bottom sheet (when product has multiple sizes) */}
      <SizeSelectBottomSheet
        isOpen={!!sizePickerItem}
        onClose={() => {
          setSizePickerItem(null);
          const pid = sizePickerItem?.id || sizePickerItem?._id;
          if (pid) {
            setItemErrors(prev => { const n = { ...prev }; delete n[pid]; return n; });
          }
        }}
        product={sizePickerItem || null}
        inStockSizes={sizePickerItem ? getInStockSizes(sizePickerItem) : []}
        onAddToCart={handleAddToCartWithSize}
        loading={sizePickerItem ? !!(itemLoading[sizePickerItem.id || sizePickerItem._id]) : false}
        error={sizePickerItem ? (itemErrors[sizePickerItem.id || sizePickerItem._id] || null) : null}
      />
    </>
  );
}

export default WishlistDrawer;
